import React from "react";
import { IconContext } from "react-icons";
import { TbBorderLeft, TbMessages, TbMessagesOff } from "react-icons/tb";
import { FiShare2 } from "react-icons/fi";
import "./ControlPanel.css";
import OpenAI from "./images/openai.png";
import HuggingFace from "./images/huggingfaceicon.png";
import anthropic from "./images/anthropic.png";
import GrokAI from "./images/GrokAI.png";
import Gemini from "./images/Google-Gemini.webp";
import Copilot from "./images/github-copilot.png";
import { ControlPanelProps } from "../types";
import ShaderGlowButton from "./ShaderGlowButton";
import { AI_MODEL_SHADER_CONFIGS } from "../config/shader-config";

const ControlPanel: React.FC<ControlPanelProps> = ({
    openAiToggle,
    toggleOpenAI,
    GrokAIToggle,
    toggleGrokAI,
    GeminiToggle,
    toggleGemini,
    flanT5Toggle,
    toggleFlanT5,
    anthropicToggle,
    toggleAnthropic,
    completionToggle,
    toggleCompletion,
    whiteboardExtend,
    toggleWhiteboard,
    toggleBorder,
    openShareModal
}) => {
    const openAiIcon = (): React.ReactElement => (
        <ShaderGlowButton
            isActive={openAiToggle}
            onClick={toggleOpenAI}
            title="OpenAI"
            glowColor={AI_MODEL_SHADER_CONFIGS.openai.color}
            intensity={AI_MODEL_SHADER_CONFIGS.openai.intensity}
            className="extendBtn"
        >
            <img
                className={openAiToggle ? "openai" : "openai-off"}
                src={OpenAI}
                alt="openai"
            />
        </ShaderGlowButton>
    );

    const GrokAIIcon = (): React.ReactElement => (
        <ShaderGlowButton
            isActive={GrokAIToggle}
            onClick={toggleGrokAI}
            title="Grok AI"
            glowColor={AI_MODEL_SHADER_CONFIGS.grok.color}
            intensity={AI_MODEL_SHADER_CONFIGS.grok.intensity}
            className="extendBtn"
        >
            <img
                className={GrokAIToggle ? "openai" : "openai-off"}
                src={GrokAI}
                alt="Grok AI"
            />
        </ShaderGlowButton>
    );

    const GeminiIcon = (): React.ReactElement => (
        <ShaderGlowButton
            isActive={GeminiToggle}
            onClick={toggleGemini}
            title="Google Gemini"
            glowColor={AI_MODEL_SHADER_CONFIGS.gemini.color}
            intensity={AI_MODEL_SHADER_CONFIGS.gemini.intensity}
            className="extendBtn"
        >
            <img
                className={GeminiToggle ? "openai" : "openai-off"}
                src={Gemini}
                alt="Gemini"
            />
        </ShaderGlowButton>
    );

    const huggingFaceIcon = (): React.ReactElement => (
        <ShaderGlowButton
            isActive={flanT5Toggle}
            onClick={toggleFlanT5}
            title="Huggingface"
            glowColor={AI_MODEL_SHADER_CONFIGS.huggingface.color}
            intensity={AI_MODEL_SHADER_CONFIGS.huggingface.intensity}
            className="extendBtn"
        >
            <img
                className={flanT5Toggle ? "openai" : "openai-off"}
                src={HuggingFace}
                alt="huggingface"
            />
        </ShaderGlowButton>
    );

    const anthropicIcon = (): React.ReactElement => (
        <ShaderGlowButton
            isActive={anthropicToggle}
            onClick={toggleAnthropic}
            title="Anthropic"
            glowColor={AI_MODEL_SHADER_CONFIGS.anthropic.color}
            intensity={AI_MODEL_SHADER_CONFIGS.anthropic.intensity}
            className="extendBtn"
        >
            <img
                className={anthropicToggle ? "openai" : "openai-off"}
                src={anthropic}
                alt="Anthropic"
            />
        </ShaderGlowButton>
    );

    const completionsIcon = (): React.ReactElement => (
        <ShaderGlowButton
            isActive={completionToggle}
            onClick={toggleCompletion}
            title="Copilot"
            glowColor={AI_MODEL_SHADER_CONFIGS.copilot.color}
            intensity={AI_MODEL_SHADER_CONFIGS.copilot.intensity}
            className="extendBtn"
        >
            <img
                className={completionToggle ? "openai" : "openai-off"}
                src={Copilot}
                alt="Copilot"
            />
        </ShaderGlowButton>
    );

    const whiteboardIcon = (): React.ReactElement => {
        const Icon = whiteboardExtend ? TbMessagesOff : TbMessages;

        return (
            <ShaderGlowButton
                isActive={whiteboardExtend}
                onClick={toggleWhiteboard}
                title="whiteboard"
                glowColor={AI_MODEL_SHADER_CONFIGS.whiteboard.color}
                intensity={AI_MODEL_SHADER_CONFIGS.whiteboard.intensity}
                className="extendBtn"
            >
                <IconContext.Provider value={{ size: "1.15em", style: { paddingRight: "0.15rem", marginTop: "0.05rem" } }}>
                    <Icon />
                </IconContext.Provider>
            </ShaderGlowButton>
        );
    };

    const borderIcon = (): React.ReactElement | null => {
        if (!whiteboardExtend) return null;

        return (
            <ShaderGlowButton
                isActive={true}
                onClick={toggleBorder}
                title="border"
                glowColor={AI_MODEL_SHADER_CONFIGS.border.color}
                intensity={AI_MODEL_SHADER_CONFIGS.border.intensity}
                className="extendBtn"
            >
                <IconContext.Provider value={{ size: "1.15em", style: { paddingRight: "0.15rem", marginTop: "0.05rem" } }}>
                    <TbBorderLeft />
                </IconContext.Provider>
            </ShaderGlowButton>
        );
    };

    const shareIcon = (): React.ReactElement => (
        <ShaderGlowButton
            isActive={false}
            onClick={openShareModal}
            title="Share this sandbox"
            glowColor={AI_MODEL_SHADER_CONFIGS.share.color}
            intensity={AI_MODEL_SHADER_CONFIGS.share.intensity}
            className="extendBtn"
        >
            <IconContext.Provider value={{ size: "1.15em", style: { paddingRight: "0.15rem", marginTop: "0.05rem" } }}>
                <FiShare2 />
            </IconContext.Provider>
        </ShaderGlowButton>
    );

    return (
        <div className={`control-panel ${whiteboardExtend ? "agent-panel-open" : ""}`}>
            {openAiIcon()}
            {anthropicIcon()}
            {GeminiIcon()}
            {GrokAIIcon()}
            {huggingFaceIcon()}
            {completionsIcon()}
            {whiteboardIcon()}
            {borderIcon()}
            {shareIcon()}
        </div>
    );
};

export default ControlPanel;
